import {
  Badge,
  Box,
  Grid,
  HStack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';

import { TODAYS_EDGE_PREVIEW_ITEMS } from './fixtures/marketingPreviewData';
import type { TodaysEdgePreviewItem } from './fixtures/marketingPreviewData';

const SPORT_LABELS: Record<string, string> = {
  nba: 'NBA',
  pfb: 'NFL',
  mlb: 'MLB',
  nhl: 'NHL',
  cfb: 'CFB',
  mbb: 'CBB',
};

function confidenceColor(c: TodaysEdgePreviewItem['confidence']): string {
  if (c === 'high') return 'green';
  if (c === 'medium') return 'yellow';
  return 'gray';
}

function confidenceHint(c: TodaysEdgePreviewItem['confidence']): string {
  if (c === 'high') return 'Large sample and strong edge';
  if (c === 'medium') return 'Solid sample and meaningful edge';
  return 'Smaller sample — treat as directional';
}

function EdgeCardLite({ item }: { item: TodaysEdgePreviewItem }) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const muted = useColorModeValue('gray.600', 'gray.400');
  const favorLabel = item.direction === 'over' ? 'Favor OVER' : 'Favor UNDER';
  const favorScheme = item.direction === 'over' ? 'green' : 'blue';
  const sportLabel = SPORT_LABELS[item.sport] ?? item.sport.toUpperCase();

  return (
    <Box
      p={4}
      borderRadius="xl"
      borderWidth="1px"
      borderColor={borderColor}
      bg={cardBg}
      boxShadow="sm"
      h="100%"
      minW={0}
      maxW="100%"
      overflow="hidden"
    >
      <HStack justify="space-between" mb={2} flexWrap="wrap" gap={2}>
        <Badge colorScheme={favorScheme} fontSize="xs" fontWeight="bold">
          {favorLabel}
        </Badge>
        {item.has_games_today ? (
          <Badge colorScheme="teal" variant="subtle" fontSize="xs">
            Games today
          </Badge>
        ) : null}
      </HStack>
      <Text fontWeight="bold" fontSize="md" mb={2}>
        {sportLabel} · {item.category_label}
      </Text>
      <Text fontSize="sm" color={muted} mb={3} lineHeight="short">
        When tracked experts pick{' '}
        <Text as="span" fontWeight="semibold" textTransform="uppercase">
          {item.direction}
        </Text>{' '}
        on this stat (last 30 days), those picks win{' '}
        <Text as="span" fontWeight="semibold" color="green.500">
          {item.win_pct.toFixed(1)}%
        </Text>{' '}
        of the time.
      </Text>
      <Text fontSize="2xl" fontWeight="extrabold" color="green.500">
        +{item.edge_pct.toFixed(1)}% edge
      </Text>
      <Text fontSize="sm" color={muted}>
        vs 50% break-even · n={item.total_picks.toLocaleString()}
      </Text>
      <Badge
        mt={3}
        colorScheme={confidenceColor(item.confidence)}
        fontSize="2xs"
        variant="subtle"
        display="block"
        w="full"
        whiteSpace="normal"
        textAlign="center"
        py={1}
        px={2}
        lineHeight="short"
      >
        {item.confidence} confidence — {confidenceHint(item.confidence)}
      </Badge>
    </Box>
  );
}

export function TodaysEdgePreview() {
  const muted = useColorModeValue('gray.600', 'gray.400');
  const labelColor = useColorModeValue('primary.600', 'primary.300');

  return (
    <Box w="100%" minW={0} overflow="hidden">
      <Text
        fontSize="xs"
        fontWeight="bold"
        letterSpacing="0.12em"
        textTransform="uppercase"
        color={labelColor}
        mb={3}
      >
        Today&apos;s edge
      </Text>
      <Text fontSize="sm" color={muted} mb={4} lineHeight="tall">
        Top over/under patterns for sports with games today. Each card shows a
        stat where expert{' '}
        <Text as="span" fontWeight="semibold">
          OVER
        </Text>{' '}
        or{' '}
        <Text as="span" fontWeight="semibold">
          UNDER
        </Text>{' '}
        picks have beaten break-even (last 30 days, 50+ picks on that side).
      </Text>
      <Grid
        templateColumns={{
          base: '1fr',
          md: 'repeat(2, minmax(0, 1fr))',
          xl: 'repeat(3, minmax(0, 1fr))',
        }}
        gap={4}
        w="100%"
        minW={0}
      >
        {TODAYS_EDGE_PREVIEW_ITEMS.map((item) => (
          <Box
            key={`${item.sport}-${item.category_label}-${item.direction}`}
            minW={0}
          >
            <EdgeCardLite item={item} />
          </Box>
        ))}
      </Grid>
    </Box>
  );
}
